/**
 * Insights — Adoption tab
 *
 * Honest version. Real data: /insights-module/summary +
 * /insights-module/time-series (DAU). WAU/MAU rolling aggregates,
 * retention cohorts, department rollout, power-user histogram,
 * leaderboards, and use cases have no backend yet — rendered as
 * empty-state placeholders.
 *
 * Backend gaps tracked separately — see MOCK-INVENTORY-2026-05-06.md.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Users, UserPlus, TrendingUp, Activity, Building2, Crown, Sparkles,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, Legend,
} from 'recharts';
import {
  KpiCard, ChartCard, FilterBar, ActionItemsDrawer, EmptySection,
  filtersToParams, rangeLabel, rangeEmptyStatePhrase,
  useInsightsSearchFilters, validateInsightsSearch,
} from '@/components/insights';
import {
  useInsightsTimeSeries,
  useInsightsSummary,
} from '@/lib/use-insights';

export const Route = createFileRoute('/insights/adoption')({
  component: AdoptionTab,
  // INS-FILTER-PERSIST-001 — URL-bind filter state per resolved decision #9.
  validateSearch: validateInsightsSearch,
});

function AdoptionTab() {
  const [filters, setFilters] = useInsightsSearchFilters('/insights/adoption');
  const [actionItemsOpen, setActionItemsOpen] = useState(false);

  // R3-P0 defect #1 — FilterBar filters propagate to backend.
  const filterParams = filtersToParams(filters);
  // Pod-KK #111 — labels follow the FilterBar range.
  const rl = rangeLabel(filters.range);
  // Bug #1117 — full-sentence range phrase for empty-state copy.
  const rp = rangeEmptyStatePhrase(filters.range);
  const { data: summary, isLoading: summaryLoading } = useInsightsSummary(filterParams);
  const { data: dauSeries = [] } = useInsightsTimeSeries('dau', 30, filterParams);

  const dau =
    dauSeries.length > 0 ? dauSeries[dauSeries.length - 1].value : 0;
  const dauPrev =
    dauSeries.length >= 8 ? dauSeries[dauSeries.length - 8].value : 0;
  const uniqueUsers30d = summary?.uniqueUsers30d ?? 0;

  const trendData = dauSeries.map((p) => ({
    date: p.date,
    DAU: p.value,
  }));

  return (
    <div className="flex flex-col h-full">
      <FilterBar
        filters={filters}
        onChange={setFilters}
        actionItemsCount={0}
        onOpenActionItems={() => setActionItemsOpen(true)}
      />

      {/* Pod-KK #613 — explicit custom-range fallback notice. */}
      {filterParams.customFallback && (
        <div
          role="status"
          className="mx-6 mt-3 rounded-md border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-200"
        >
          Custom date ranges aren't supported yet — showing the last 7
          days. Pick a preset range to filter precisely.
        </div>
      )}

      <div className="flex-1 p-6 space-y-6">
        {/* KPIs — real where available */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard
            label="Daily Active (latest)"
            icon={Users}
            value={summaryLoading ? '—' : dau.toLocaleString()}
            numericValue={dau}
            prevValue={dauPrev}
            sparkline={dauSeries.slice(-14).map((p) => p.value)}
            subtitle="Distinct users on most recent day"
          />
          <KpiCard
            label={`Unique Users (${rl})`}
            icon={Users}
            value={summaryLoading ? '—' : uniqueUsers30d.toLocaleString()}
            numericValue={uniqueUsers30d}
            subtitle="From query_metrics"
          />
          <KpiCard
            label="Weekly Active"
            icon={Users}
            value="—"
            subtitle="Rolling-window aggregate not wired"
          />
          <KpiCard
            label="New Users (7d)"
            icon={UserPlus}
            value="—"
            subtitle="Account-creation feed not wired"
          />
        </div>

        {/* DAU trend — real */}
        <ChartCard title={`Daily active users (${rl})`} icon={TrendingUp}>
          {trendData.length === 0 ? (
            <EmptySection
              message={`No DAU data ${rp}.`}
              hint="DAU is computed as distinct user_id per day from query_metrics."
              minHeight="min-h-56"
            />
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v) =>
                      new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    }
                  />
                  <YAxis tick={{ fontSize: 10 }} width={40} />
                  <Tooltip
                    contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid hsl(var(--border))' }}
                    labelFormatter={(v) =>
                      new Date(v).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                    }
                  />
                  <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                  <Line type="monotone" dataKey="DAU" stroke="rgb(99 102 241)" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartCard>

        {/* Empty-state sections for missing backends.
            Phrasing intentionally matches the canonical honest-placeholder
            vocabulary asserted in tests/playwright/specs/smoke-insights.spec.ts
            (INSIGHTS-ADOPT-002) — one of: "not yet available", "not wired",
            "no <name> engine", "requires <name> job". Keep these strings in
            sync with adoption-honest-copy.test.ts. */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Retention cohort" icon={Activity}>
            <EmptySection
              message="Cohort retention not yet available."
              hint="Requires a weekly-cohort bucketing job that tracks week-N return rates from query_metrics."
              minHeight="min-h-56"
            />
          </ChartCard>

          <ChartCard title="Queries per user (30d)" icon={Sparkles}>
            <EmptySection
              message="Power-user histogram not wired."
              hint="Requires a per-user query-count distribution endpoint."
              minHeight="min-h-56"
            />
          </ChartCard>
        </div>

        <ChartCard title="Department rollout" icon={Building2}>
          <EmptySection
            message="Department / team rollout not yet available."
            hint="Requires a department-taxonomy rollup job plus license-vs-active counts per department."
          />
        </ChartCard>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Top power users (30d)" icon={Crown}>
            <EmptySection
              message="Power-user leaderboard not wired."
              hint="Requires a per-user query-count endpoint joined with the user profile."
            />
          </ChartCard>

          <ChartCard title="Top use cases (30d)" icon={Sparkles}>
            <EmptySection
              message="Use-case clustering not yet available."
              hint="Requires an NLP topic-clustering job over the last 30 days of queries."
            />
          </ChartCard>
        </div>
      </div>

      <ActionItemsDrawer
        open={actionItemsOpen}
        onOpenChange={setActionItemsOpen}
        items={[]}
      />
    </div>
  );
}
